import { Role, isRole } from '@worklogix/shared';
import { useAuth } from '@worklogix/web-common';

/** Read the loosely-typed MSAL `roles` claim as string[] (undefined = absent). */
function readRoleClaim(claims: unknown): string[] | undefined {
  if (typeof claims !== 'object' || claims === null || !('roles' in claims)) {
    return undefined;
  }
  const roles = (claims as { roles?: unknown }).roles;
  if (!Array.isArray(roles)) {
    return undefined;
  }
  return roles.filter((r): r is string => typeof r === 'string');
}

/**
 * True when the signed-in user is VIEW ONLY — the base Employee app role WITHOUT
 * Approver (Manager) or Administrator (Admin). Fail-CLOSED (absent/ambiguous claim
 * → false). Drives the read-only surface limited to Hours & Timesheet; the API is
 * the authoritative gate (View-Only tokens 403 on every write regardless).
 */
export function useIsViewer(): boolean {
  const { account } = useAuth();
  const roles = readRoleClaim(account?.idTokenClaims);
  if (roles === undefined) {
    return false;
  }
  const has = (role: Role): boolean => roles.some((r) => isRole(r) && r === role);
  return has(Role.Employee) && !has(Role.Manager) && !has(Role.Admin);
}
